import { c as _c } from "react/compiler-runtime";
import { getErrorMessage } from '@onefootprint/request';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { DialogPage, LoadingSpinner } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import DocumentDetailsDialog from '@/entity/components/content/components/document-details-dialog';
import DocumentViewerContainer from '@/entity/components/content/components/document-item/components/document-viewer-container';
import DecryptForm from '@/entity/components/decrypt-form';
import useDocumentsQueryParams from '@/entity/hooks/use-documents-query-params';
import useVaultDocument from '../../hooks/use-vault-document';
const ViewerStatus = t0 => {
  const $ = _c(5);
  const {
    children,
    onClose
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex h-full items-center justify-center p-6 text-body-2 text-secondary">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onClose || $[3] !== t1) {
    t2 = <DialogPage title="Vault document" onClose={onClose}>{t1}</DialogPage>;
    $[2] = onClose;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const InvestigationDocumentViewerContent = t0 => {
  const $ = _c(34);
  const {
    documentIdentifier,
    entity,
    onClose
  } = t0;
  const result = useVaultDocument(entity.id, documentIdentifier);
  let t1;
  if ($[0] !== entity.data || $[1] !== entity.id || $[2] !== result.version) {
    t1 = {
      entityAttributes: entity.data,
      entityId: entity.id,
      seqno: result.version
    };
    $[0] = entity.data;
    $[1] = entity.id;
    $[2] = result.version;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const vault = useEntityVault(t1);
  if (result.isLoading || vault.isPending) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <LoadingSpinner />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== onClose) {
      t3 = <ViewerStatus onClose={onClose}>{t2}</ViewerStatus>;
      $[5] = onClose;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const error = result.error ?? vault.error;
  if (error) {
    let t2;
    if ($[7] !== error) {
      t2 = getErrorMessage(error);
      $[7] = error;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== onClose || $[10] !== t2) {
      t3 = <ViewerStatus onClose={onClose}>{t2}</ViewerStatus>;
      $[9] = onClose;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  if (result.isUnavailable || !result.match || !vault.data) {
    let t2;
    if ($[12] !== onClose) {
      t2 = <ViewerStatus onClose={onClose}>This exact document version is unavailable.</ViewerStatus>;
      $[12] = onClose;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    return t2;
  }
  const {
    document,
    upload
  } = result.match;
  if (!document.canDecrypt) {
    let t2;
    if ($[14] !== onClose) {
      t2 = <ViewerStatus onClose={onClose}>You do not have permission to decrypt this document.</ViewerStatus>;
      $[14] = onClose;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    return t2;
  }
  let t2;
  if ($[16] !== upload) {
    t2 = [upload];
    $[16] = upload;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] !== document || $[19] !== documentIdentifier || $[20] !== t2) {
    t3 = {
      ...document,
      id: documentIdentifier,
      uploads: t2
    };
    $[18] = document;
    $[19] = documentIdentifier;
    $[20] = t2;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  const pinnedDocument = t3;
  if (pinnedDocument.kind === "custom") {
    let t4;
    if ($[22] !== documentIdentifier || $[23] !== entity || $[24] !== pinnedDocument || $[25] !== result.version || $[26] !== vault) {
      t4 = <DocumentViewerContainer key={documentIdentifier} document={pinnedDocument} entity={entity} vault={vault} seqno={result.version} />;
      $[22] = documentIdentifier;
      $[23] = entity;
      $[24] = pinnedDocument;
      $[25] = result.version;
      $[26] = vault;
      $[27] = t4;
    } else {
      t4 = $[27];
    }
    return t4;
  }
  let t4;
  if ($[28] !== documentIdentifier || $[29] !== entity || $[30] !== pinnedDocument || $[31] !== result.version || $[32] !== vault) {
    t4 = <DocumentDetailsDialog key={documentIdentifier} document={pinnedDocument} entity={entity} vault={vault} seqno={result.version} />;
    $[28] = documentIdentifier;
    $[29] = entity;
    $[30] = pinnedDocument;
    $[31] = result.version;
    $[32] = vault;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  return t4;
};
const InvestigationDocumentViewer = t0 => {
  const $ = _c(4);
  const {
    entity
  } = t0;
  const {
    values,
    clear
  } = useDocumentsQueryParams();
  if (!entity || !values.documentId) {
    return null;
  }
  let t1;
  if ($[0] !== clear || $[1] !== entity || $[2] !== values.documentId) {
    t1 = <DecryptForm><InvestigationDocumentViewerContent documentIdentifier={values.documentId} entity={entity} onClose={clear} /></DecryptForm>;
    $[0] = clear;
    $[1] = entity;
    $[2] = values.documentId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export default InvestigationDocumentViewer;